/**
 * Datos canónicos de la UI de nivelación (copy + display), portados del bundle
 * de diseño `level-data.jsx`.
 *
 * NO contiene la lógica de recomendación ni el mapeo CEFR↔intensidad: eso vive
 * en el backend y llega vía `getEnglishState` (`recommendedIntensity`,
 * `effectiveIntensity`). Acá sólo viven los textos y el orden visual. La unión
 * de intensidades es la fuente única de `@orborix/shared-types`
 * (`ENGLISH_INTENSITIES`); este módulo le pone labels/descripciones.
 */
import { ENGLISH_INTENSITIES, type EnglishIntensity } from '@orborix/shared-types';
import type { CefrLevel, LevelDimension, LevelMap, TriageLevel } from '../../lib/englishApi.js';

// ─── Escala CEFR — un solo nivel global (el detalle por dims es opcional) ─────
export interface CefrInfo {
  id: CefrLevel;
  idx: number;
  name: string;
  blurb: string;
}

// ESPACIO DE PROFICIENCIA (A0..C2, 7 niveles): el diagnóstico del usuario. A0
// ("Primeros pasos") = pre-A1 / principiante absoluto (plan 31). Es lo que se
// muestra en el reveal, el Home, la evolución y el rail CEFR. OJO: NO es el
// ESPACIO DE CONTENIDO (libros /listen, dims del currículum, vocab), que sigue
// siendo A1..C2 — ver `CONTENT_CEFR_INFO` abajo y doc 31 §5 (dos espacios CEFR).
export const CEFR_INFO: ReadonlyArray<CefrInfo> = [
  { id: 'A0', idx: 0, name: 'Primeros pasos', blurb: 'Arrancás de cero — tus primerísimas palabras.' },
  { id: 'A1', idx: 1, name: 'Principiante', blurb: 'Tus primeras palabras y frases sueltas.' },
  { id: 'A2', idx: 2, name: 'Básico', blurb: 'Frases simples sobre lo cotidiano.' },
  { id: 'B1', idx: 3, name: 'Intermedio', blurb: 'Te defendés en charlas del día a día.' },
  { id: 'B2', idx: 4, name: 'Intermedio alto', blurb: 'Conversás con soltura sobre casi todo.' },
  { id: 'C1', idx: 5, name: 'Avanzado', blurb: 'Fluidez real, con matices y tono.' },
  { id: 'C2', idx: 6, name: 'Dominio', blurb: 'Te manejás como un hablante nativo.' },
];

/**
 * Subconjunto de CONTENIDO (A1..C2) — sin A0. Los libros de /listen, las dims
 * del currículum, el vocab y demás CONTENIDO no existen en A0 (no hay narración
 * ni units A0). Usalo donde se listan niveles de CONTENIDO, no de proficiencia;
 * meter A0 ahí correría los índices (off-by-one silencioso). Ver doc 31 §5.
 */
export const CONTENT_CEFR_INFO: ReadonlyArray<CefrInfo> = CEFR_INFO.filter((l) => l.id !== 'A0');

/**
 * Índice de A1 en `CEFR_INFO` = piso del ESPACIO DE CONTENIDO. El ajuste manual
 * del examen (`adjustLevel`) escribe niveles por-dimensión (contenido), así que
 * nunca debe bajar a A0 (idx 0, proficiencia). Ver doc 31 §5.
 */
const CONTENT_FLOOR_IDX = CEFR_INFO.findIndex((l) => l.id === 'A1');

/**
 * Avance decorativo dentro del nivel hacia el siguiente. TODO(backend): no hay
 * todavía una señal real de progreso intra-nivel; es un valor fijo para el
 * anillo/barra hasta que exista.
 */
export const LEVEL_PROGRESS_PLACEHOLDER = 0.42;

/**
 * Info de un CEFR de proficiencia. **Fail-fast**: un id desconocido tira en vez
 * de degradar a un default silencioso (regla del repo). Como `CefrLevel` es un
 * union cerrado que `CEFR_INFO` cubre entero (A0..C2), sólo dispara con data
 * corrupta.
 */
export function levelById(id: CefrLevel): CefrInfo {
  const info = CEFR_INFO.find((l) => l.id === id);
  if (!info) throw new Error(`levelById: CEFR desconocido: ${String(id)}`);
  return info;
}

export function nextLevel(id: CefrLevel): CefrInfo {
  const i = levelById(id).idx;
  return CEFR_INFO[Math.min(i + 1, CEFR_INFO.length - 1)]!;
}

export function isTopLevel(id: CefrLevel): boolean {
  return levelById(id).idx >= CEFR_INFO.length - 1;
}

/**
 * Mueve un nivel ±delta dentro del ESPACIO DE CONTENIDO (A1..C2). Lo usa el
 * ajuste manual del reveal del examen, que escribe niveles por-dimensión
 * (contenido) — por eso el piso es A1 (`CONTENT_FLOOR_IDX`), nunca A0.
 */
export function adjustLevel(id: CefrLevel, delta: number): CefrLevel {
  const i = levelById(id).idx;
  const ni = Math.max(CONTENT_FLOOR_IDX, Math.min(CEFR_INFO.length - 1, i + delta));
  return CEFR_INFO[ni]!.id;
}

/**
 * Deriva un CEFR global desde las dimensiones (promedio redondeado). Las dims
 * son CONTENIDO (A1..C2, idx 1..6): el promedio nunca cae en A0 (idx 0), así que
 * esto vive en el espacio de contenido aunque comparta `CEFR_INFO`.
 */
export function cefrFromLevels(levels: LevelMap): CefrLevel {
  const idxs = Object.values(levels).map((id) => levelById(id).idx);
  const avg = idxs.reduce((a, b) => a + b, 0) / idxs.length;
  return CEFR_INFO[Math.round(avg)]!.id;
}

/** Explicación humana del nivel, para el reveal del examen. */
export const LEVEL_EXPLAIN: Record<CefrLevel, string> = {
  A0: 'Arrancás de cero, y está perfecto: vamos a dar tus primeros pasos juntos, sin apuro.',
  A1: 'Estás dando tus primeros pasos — y eso es exactamente donde hay que empezar.',
  A2: 'Ya entendés y armás frases simples. Tenemos una base linda para construir.',
  B1: 'Te entendés bien en charlas cotidianas. Ya sostenés una conversación real.',
  B2: 'Te movés con soltura en casi cualquier tema. Estás cerca de sonar muy natural.',
  C1: 'Tu inglés fluye con matices y tono. Casi no se te nota el esfuerzo.',
  C2: 'Te manejás como un nativo. Acá venimos a pulir y mantener.',
};

// ─── Intensidad — cómo te habla el coach (6 paradas, fuente shared-types) ─────
export interface IntensityDisplay {
  id: EnglishIntensity;
  label: string;
  /** Usa español de apoyo (sólo `starter`). */
  l1Support: boolean;
  desc: string;
}

const INTENSITY_DISPLAY_MAP: Record<EnglishIntensity, Omit<IntensityDisplay, 'id'>> = {
  starter: {
    label: 'Starter',
    l1Support: true,
    desc: 'Te hablo en inglés muy simple y te tiro un cable en español cuando haga falta.',
  },
  cozy: {
    label: 'Cozy',
    l1Support: false,
    desc: 'Todo en inglés, con frases cortas y claras, y pausas para que me sigas.',
  },
  natural: {
    label: 'Natural',
    l1Support: false,
    desc: 'Ritmo de conversación real, con vocabulario cotidiano. Como charlar con alguien.',
  },
  push: {
    label: 'Push',
    l1Support: false,
    desc: 'Frases más largas y expresiones nuevas. Te saco un poquito de tu zona cómoda.',
  },
  stretch: {
    label: 'Stretch',
    l1Support: false,
    desc: 'Voy más rápido, con modismos y menos pausas. Te exijo de verdad.',
  },
  native: {
    label: 'Native',
    l1Support: false,
    desc: 'Inmersión total: sin filtros, al ritmo real de la calle.',
  },
};

/** Las 6 paradas en orden (starter → native), con su copy. */
export const INTENSITY_OPTIONS: ReadonlyArray<IntensityDisplay> = ENGLISH_INTENSITIES.map((id) => ({
  id,
  ...INTENSITY_DISPLAY_MAP[id],
}));

export function intensityById(id: EnglishIntensity): IntensityDisplay {
  return INTENSITY_OPTIONS.find((i) => i.id === id) ?? INTENSITY_OPTIONS[2]!;
}

export function intensityIndex(id: EnglishIntensity): number {
  return ENGLISH_INTENSITIES.indexOf(id);
}

// ─── Triage (A1) — una sola pregunta, sin juicio ─────────────────────────────
// Cada opción bifurca el flujo (plan 28): 'quiz' → placement quiz tap-only que
// decide el andamiaje del starter; 'exam' → oferta de mini-examen opt-in. Las
// dos opciones beginner viejas (zero/words) colapsaron en una sola puerta
// ("Recién empiezo") — el quiz decide el sub-nivel, ya no el autoreporte.
export type TriageBranch = 'quiz' | 'exam';

export interface TriageOptionUi {
  /** Id de UI (estable para keys/selección). */
  id: string;
  /**
   * Nivel que entiende el backend (`POST /triage`). Sólo para branch `'exam'`;
   * ausente en `'quiz'` (la rama dispara el placement, no asigna un nivel).
   */
  backend?: TriageLevel;
  branch: TriageBranch;
  /** Barras encendidas en el SignalMeter (0..3). */
  amount: number;
  label: string;
  sub: string;
}

export const TRIAGE_OPTIONS: ReadonlyArray<TriageOptionUi> = [
  // Rama beginner colapsada (plan 28 D2): una sola puerta → quiz de placement.
  {
    id: 'beginner',
    branch: 'quiz',
    amount: 0,
    label: 'Recién empiezo',
    sub: 'Nunca estudié inglés, o sé muy poco.',
  },
  // Recupera A2 (plan 28 D11): esta opción mapeaba a `conversational`→B1 y dejaba
  // a un A2 de manual arrancando en B1→push (intimidante). Ahora cae en `basic`→A2.
  {
    id: 'basic',
    backend: 'basic',
    branch: 'exam',
    amount: 1,
    label: 'Me defiendo en una charla simple',
    sub: 'Puedo seguir una conversación básica si van despacio.',
  },
  // Escalón B1 (el que antes ocupaba "charla simple").
  {
    id: 'conversational',
    backend: 'conversational',
    branch: 'exam',
    amount: 2,
    label: 'Mantengo conversaciones',
    sub: 'Charlo de varios temas, con errores.',
  },
  {
    id: 'fluent',
    backend: 'fluent',
    branch: 'exam',
    amount: 3,
    label: 'Hablo con bastante fluidez',
    sub: 'Conversaciones largas sin mucho esfuerzo.',
  },
];

// ─── Dimensiones (desglose opcional) ─────────────────────────────────────────
export const DIMENSION_LABELS: Record<LevelDimension, string> = {
  grammar: 'Gramática',
  vocabulary: 'Vocabulario',
  functional: 'Función comunicativa',
};

/** Orden estable de las dimensiones para el desglose. */
export const DIMENSION_ORDER: ReadonlyArray<LevelDimension> = [
  'grammar',
  'vocabulary',
  'functional',
];
